import { type SupportedLanguage, changeLanguage } from '@/i18n';
import type React from 'react';
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { endAdminAccessTab } from './admin-access';
import {
  consentRecordingApi,
  fetchMeWithToken,
  login as loginApi,
  logout as logoutApi,
  refreshTokenApi,
  resetPasswordApi,
  resetPasswordFromLink as resetPasswordFromLinkApi,
  setupInitialPasswordForHr as setupHrPasswordApi,
  setupInitialPassword as setupPasswordApi,
  verifyInitialPasswordSetupCode as verifyCodeApi,
  verifyPasswordResetCode as verifyPasswordResetCodeApi,
} from './auth-service';
import {
  type AuthData,
  type User,
  clearTokens,
  getAuthData as getStoredAuthData,
  isAdminAccessTab,
  isTokenExpired,
  markAdminAccessEnded,
  saveAuthData,
} from './auth-storage';
import { syncAuthUserWithServer } from './sync-auth-user';

// 인증 컨텍스트 타입
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
  resetPassword: (newPassword: string) => Promise<void>;
  verifyInitialPasswordSetupCode: (code: string) => Promise<any>;
  setupInitialPassword: (code: string, password: string) => Promise<any>;
  setupInitialPasswordForHrAndLogin: (
    code: string,
    password: string,
  ) => Promise<void>;
  verifyPasswordResetCode: (code: string) => Promise<any>;
  resetPasswordFromLink: (code: string, newPassword: string) => Promise<any>;
  consentRecording: (consent: boolean) => Promise<void>;
  error: string | null;
  getAuthData: () => AuthData | null;
}

// 초기 컨텍스트 값
const initialContext: AuthContextType = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => {},
  logout: () => {},
  refresh: async () => {},
  resetPassword: async () => {},
  verifyInitialPasswordSetupCode: async () => ({}),
  setupInitialPassword: async () => ({}),
  setupInitialPasswordForHrAndLogin: async () => {},
  verifyPasswordResetCode: async () => ({}),
  resetPasswordFromLink: async () => ({}),
  consentRecording: async () => {},
  error: null,
  getAuthData: () => null,
};

// 인증 컨텍스트 생성
const AuthContext = createContext<AuthContextType>(initialContext);

// 인증 컨텍스트 사용 훅
export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

// 인증 제공자 컴포넌트
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authData, setAuthData] = useState<AuthData | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // 마지막으로 서버 사용자 정보와 맞춘 시각. 창 포커스 때 너무 자주 확인하지 않게 한다.
  const lastUserSyncAtRef = useRef(0);

  // 언어 설정 함수
  const setUserLanguage = (lang: SupportedLanguage) => {
    changeLanguage(lang);
  };

  // 초기 인증 상태 확인 및 설정
  useEffect(() => {
    async function initAuth() {
      try {
        // magic link: URL 쿼리에 accessToken/refreshToken이 있으면 우선 처리
        const searchParams = new URLSearchParams(window.location.search);
        const urlAccessToken = searchParams.get('accessToken');
        const urlRefreshToken = searchParams.get('refreshToken');

        if (urlAccessToken && urlRefreshToken) {
          try {
            const user = await fetchMeWithToken(urlAccessToken);
            const newAuthData: AuthData = {
              user: {
                id: user.id,
                email: user.email,
                name: user.name,
                lang: user.lang,
                roles: user.roles || [],
                companyId: user.companyId,
                isRecordingConsented: user.isRecordingConsented ?? null,
                passwordChange: user.passwordChange,
              },
              tokens: {
                accessToken: urlAccessToken,
                refreshToken: urlRefreshToken,
                expiresIn: 3600,
              },
            };
            saveAuthData(newAuthData);
            setAuthData(newAuthData);
            setIsAuthenticated(true);
            setUserLanguage(user.lang as SupportedLanguage);

            // URL에서 토큰 파라미터 제거
            const cleanUrl = new URL(window.location.href);
            cleanUrl.searchParams.delete('accessToken');
            cleanUrl.searchParams.delete('refreshToken');
            window.history.replaceState({}, '', cleanUrl.toString());
          } catch {
            setIsAuthenticated(false);
          }
          setIsLoading(false);
          return;
        }

        // 로컬 스토리지에서 직접 데이터 확인
        const storedData = getStoredAuthData();

        if (storedData && storedData.user && storedData.tokens) {
          if (!storedData.user.roles?.length) {
            // roles가 비어있으면 원인 불명이므로 로그아웃 처리
            clearTokens();
            setAuthData(null);
            setIsAuthenticated(false);
            setIsLoading(false);
            return;
          }

          // 대리 접속 탭은 리프레시 토큰이 없어 만료되면 되살리지 않는다.
          if (isAdminAccessTab() && isTokenExpired()) {
            markAdminAccessEnded('EXPIRED');
            setAuthData(null);
            setIsAuthenticated(false);
            return;
          }

          let current: AuthData = storedData;
          let tokensJustIssued = false;
          if (isTokenExpired()) {
            // 토큰 만료 시 refresh 먼저 시도
            try {
              const newTokens = await refreshTokenApi();
              current = { ...storedData, tokens: newTokens };
              saveAuthData(current);
              tokensJustIssued = true;
            } catch {
              setAuthData(null);
              setIsAuthenticated(false);
              return;
            }
          }

          // 저장된 사용자 정보는 로그인 시점 값이라, DB에서 역할이 바뀌어도 그대로 남는다.
          // 화면을 그리기 전에(isLoading 유지) 서버 값으로 맞춰야 바뀐 역할로 라우팅된다.
          const synced = await syncAuthUserWithServer(current, {
            tokensJustIssued,
          });
          lastUserSyncAtRef.current = Date.now();
          if (synced.kind === 'signedOut') {
            clearTokens();
            setAuthData(null);
            setIsAuthenticated(false);
            return;
          }
          if (synced.kind === 'updated') current = synced.authData;

          setAuthData(current);
          setIsAuthenticated(true);
          setUserLanguage(current.user.lang as SupportedLanguage);
        } else {
          // 저장된 인증 데이터가 없으면 로그아웃 상태로 설정
          setAuthData(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    }

    initAuth();
  }, []);

  // 로그인 함수
  const login = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await loginApi({ email, password });

      // 유효한 응답인지 확인
      if (!response?.user || !response?.tokens) {
        throw new Error('로그인 응답 구조가 올바르지 않습니다');
      }

      // 새로운 인증 데이터 구성
      const newAuthData: AuthData = {
        user: {
          id: response.user.id,
          email: response.user.email,
          name: response.user.name,
          lang: response.user.lang,
          roles: response.user.roles || [],
          companyId: response.user.companyId,
          isRecordingConsented: response.user.isRecordingConsented ?? null,
          passwordChange: response.user.passwordChange,
        },
        tokens: {
          accessToken: response.tokens.accessToken,
          refreshToken: response.tokens.refreshToken,
          expiresIn: response.tokens.expiresIn,
        },
      };

      // 상태 업데이트
      setAuthData(newAuthData);
      setIsAuthenticated(true);

      // 사용자 언어 설정 적용
      setUserLanguage(newAuthData.user.lang as SupportedLanguage);

      // 로컬 스토리지에 인증 데이터 저장
      saveAuthData(newAuthData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '로그인 실패';
      console.error('Login error details:', err);
      setError(errorMessage);
      setIsAuthenticated(false);
      setAuthData(null);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // 리프레시 토큰 함수
  const refresh = async () => {
    if (!authData?.tokens?.refreshToken) {
      throw new Error('리프레시 토큰이 없습니다');
    }

    setIsLoading(true);
    try {
      const tokens = await refreshTokenApi();

      // 기존 사용자 정보 유지하면서 토큰만 업데이트
      if (authData.user) {
        const updatedAuthData = {
          user: authData.user,
          tokens,
        };

        setAuthData(updatedAuthData);
        setIsAuthenticated(true);

        // 사용자 언어 설정 적용
        setUserLanguage(updatedAuthData.user.lang as SupportedLanguage);

        // 로컬 스토리지에 업데이트된 인증 데이터 저장
        saveAuthData(updatedAuthData);
      }
    } catch (error) {
      logout();
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 비밀번호 변경.
   *
   * 서버가 새 토큰과 갱신된 변경 주기 상태를 함께 내려준다. 기존 토큰에는 변경 전
   * 기준 시각이 박혀 있어 갈아끼우지 않으면 변경 직후에도 계속 차단된다.
   */
  const resetPassword = async (newPassword: string) => {
    if (!authData?.tokens?.accessToken) {
      throw new Error('비밀번호 재설정을 위해 로그인이 필요합니다');
    }

    // 전역 isLoading 은 건드리지 않는다. ProtectedRoute 가 isLoading 이면 null 을
    // 돌려주기 때문에, 여기서 켜면 비밀번호를 바꾸는 동안 보호 영역이 통째로
    // 언마운트되고 화면이 들고 있던 상태(변경 완료 안내 등)가 날아간다.
    // 진행 중 표시는 호출하는 화면이 각자 하고 있다.
    const response = await resetPasswordApi(
      newPassword,
      authData.tokens.accessToken,
    );

    if (response?.tokens && authData.user) {
      const updatedAuthData: AuthData = {
        user: {
          ...authData.user,
          passwordChange: response.user?.passwordChange,
        },
        tokens: response.tokens,
      };
      setAuthData(updatedAuthData);
      saveAuthData(updatedAuthData);
    }
  };

  // 초기 비밀번호 설정 코드 검증 함수
  const verifyInitialPasswordSetupCode = async (code: string) => {
    setIsLoading(true);
    try {
      const response = await verifyCodeApi(code);
      return response;
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // 초기 비밀번호 설정 함수
  const setupInitialPassword = async (code: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await setupPasswordApi(code, password);
      return response;
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // HR 비밀번호 설정 + 자동 로그인 (토큰을 받아 로그인 상태로 만든다)
  const setupInitialPasswordForHrAndLogin = async (
    code: string,
    password: string,
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await setupHrPasswordApi(code, password);
      if (!response?.user || !response?.tokens) {
        throw new Error('로그인 응답 구조가 올바르지 않습니다');
      }

      const newAuthData: AuthData = {
        user: {
          id: response.user.id,
          email: response.user.email,
          name: response.user.name,
          lang: response.user.lang,
          roles: response.user.roles || [],
          companyId: response.user.companyId,
          isRecordingConsented: response.user.isRecordingConsented ?? null,
          passwordChange: response.user.passwordChange,
        },
        tokens: {
          accessToken: response.tokens.accessToken,
          refreshToken: response.tokens.refreshToken,
          expiresIn: response.tokens.expiresIn,
        },
      };

      setAuthData(newAuthData);
      setIsAuthenticated(true);
      setUserLanguage(newAuthData.user.lang as SupportedLanguage);
      saveAuthData(newAuthData);
    } catch (err) {
      setIsAuthenticated(false);
      setAuthData(null);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // 비밀번호 재설정 링크 검증 함수
  const verifyPasswordResetCode = async (code: string) => {
    setIsLoading(true);
    try {
      const response = await verifyPasswordResetCodeApi(code);
      return response;
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // 비밀번호 재설정 함수
  const resetPasswordFromLink = async (code: string, newPassword: string) => {
    setIsLoading(true);
    try {
      const response = await resetPasswordFromLinkApi(code, newPassword);
      return response;
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // 전사 및 AI 요약 동의 처리
  const consentRecording = async (consent: boolean) => {
    await consentRecordingApi(consent);
    if (authData?.user) {
      const updatedAuthData = {
        ...authData,
        user: { ...authData.user, isRecordingConsented: consent },
      };
      setAuthData(updatedAuthData);
      saveAuthData(updatedAuthData);
    }
  };

  // 로그아웃 함수
  const logout = () => {
    // 대리 접속 탭의 로그아웃은 대리 접속 종료다. 원래 탭의 CPM 로그인은 건드리지 않는다.
    if (isAdminAccessTab()) {
      // 실패하면 안내 토스트가 뜨고 이 탭은 그대로 대리 접속 중으로 남는다.
      endAdminAccessTab().catch(() => {});
      return;
    }
    logoutApi();
    setAuthData(null);
    setIsAuthenticated(false);
    clearTokens(); // 로컬 스토리지에서 인증 데이터 제거
  };

  // 인증 데이터 접근 함수
  const getAuthData = () => {
    // 메모리에 있는 상태를 우선 반환
    if (authData) {
      return authData;
    }

    // 메모리에 없으면 스토리지에서 직접 가져옴
    return getStoredAuthData();
  };

  // 탭을 열어둔 채 쓰는 동안 바뀐 역할도 반영한다. 다른 창에 있다가 돌아올 때 확인하고,
  // 화면을 내리지 않도록 isLoading은 건드리지 않는다.
  useEffect(() => {
    if (!isAuthenticated) return;

    const syncOnReturn = async () => {
      if (document.visibilityState === 'hidden') return;
      if (Date.now() - lastUserSyncAtRef.current < USER_SYNC_MIN_INTERVAL_MS) {
        return;
      }
      const stored = getStoredAuthData();
      if (!stored?.user || !stored.tokens) return;

      lastUserSyncAtRef.current = Date.now();
      const result = await syncAuthUserWithServer(stored);
      if (result.kind === 'updated') {
        setAuthData(result.authData);
        changeLanguage(result.authData.user.lang as SupportedLanguage);
      } else if (result.kind === 'signedOut') {
        // logout()과 같은 처리. 렌더마다 새로 만들어지는 함수를 효과에 묶지 않으려고 직접 부른다.
        logoutApi();
        clearTokens();
        setAuthData(null);
        setIsAuthenticated(false);
      }
    };

    window.addEventListener('focus', syncOnReturn);
    document.addEventListener('visibilitychange', syncOnReturn);
    return () => {
      window.removeEventListener('focus', syncOnReturn);
      document.removeEventListener('visibilitychange', syncOnReturn);
    };
  }, [isAuthenticated]);

  // 컨텍스트 값
  const contextValue: AuthContextType = {
    user: authData?.user || null,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refresh,
    resetPassword,
    verifyInitialPasswordSetupCode,
    setupInitialPassword,
    setupInitialPasswordForHrAndLogin,
    verifyPasswordResetCode,
    resetPasswordFromLink,
    consentRecording,
    error,
    getAuthData,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

/** 창 포커스 때 서버 사용자 정보를 다시 확인하는 최소 간격. 탭을 오갈 때마다 요청하지 않게 한다. */
const USER_SYNC_MIN_INTERVAL_MS = 60_000;
